<template>
    <div class="pqr-rich-editor" :class="{ 'is-dragging': dragging }">
        <div ref="editorEl"></div>
        <div v-if="uploading > 0" class="text-xs text-info mt-1">
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            Subiendo {{ uploading }} {{ uploading === 1 ? 'imagen' : 'imagenes' }}...
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { getErrorMessage } from '@/store/pqrStore';
import { isHtml } from '@/libs/sanitizeHtml';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Solo formatos que el backend conserva al limpiar el HTML
const TOOLBAR = [
    ['bold', 'italic', 'underline', 'strike'],
    [{ header: [2, 3, false] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    ['clean'],
];
const FORMATS = ['bold', 'italic', 'underline', 'strike', 'header', 'list', 'blockquote', 'code-block', 'link', 'image'];

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export default {
    name: 'PqrRichEditor',
    props: {
        pqrId: { type: [Number, String], required: true },
        placeholder: { type: String, default: 'Escriba la respuesta para el solicitante...' },
    },
    emits: ['change', 'uploading'],
    setup(props, { emit }) {
        const store = useStore();
        const editorEl = ref(null);
        const uploading = ref(0);
        const dragging = ref(false);
        let quill = null;

        // Imágenes subidas en esta sesión del editor y que aún no se han enviado
        let pendientes = [];

        const setUploading = (value) => {
            uploading.value = value;
            emit('uploading', value);
        };

        const findImageIndex = (src) => {
            const img = Array.from(quill.root.querySelectorAll('img')).find((el) => el.getAttribute('src') === src);
            if (!img) return -1;
            const blot = Quill.find(img);
            return blot ? quill.getIndex(blot) : -1;
        };

        const descartar = (imagenes) => Promise.all(imagenes.map((img) =>
            store.dispatch('appPQR/deletePQRImageAction', { pqrId: props.pqrId, imagenId: img.id })
                .catch((error) => console.error('Error deleting PQR image:', error))
        ));

        const subirImagen = async (file, previa) => {
            setUploading(uploading.value + 1);
            try {
                const data = await store.dispatch('appPQR/uploadPQRImageAction', { pqrId: props.pqrId, file });
                pendientes.push({ id: data.id, url: data.url });

                const i = findImageIndex(previa);
                if (i >= 0) {
                    quill.deleteText(i, 1, 'api');
                    quill.insertEmbed(i, 'image', data.url, 'api');
                } else {
                    // La borraron mientras subía: ya no hace falta en el servidor
                    pendientes = pendientes.filter((img) => img.id !== data.id);
                    descartar([data]);
                }
            } catch (error) {
                const i = findImageIndex(previa);
                if (i >= 0) quill.deleteText(i, 1, 'api');
                alert(`No se pudo subir ${file.name}: ` + getErrorMessage(error));
            } finally {
                setUploading(uploading.value - 1);
            }
        };

        // Inserta las vistas previas en orden y sube todas en paralelo
        const insertarArchivos = async (files) => {
            const range = quill.getSelection(true);
            let index = range ? range.index : quill.getLength() - 1;
            const rechazadas = [];

            for (const file of files) {
                if (!ALLOWED_TYPES.includes(file.type)) {
                    rechazadas.push(`${file.name}: solo se permiten imagenes jpeg, png, gif o webp`);
                    continue;
                }
                if (file.size > MAX_IMAGE_SIZE) {
                    rechazadas.push(`${file.name}: pesa mas de 5 MB`);
                    continue;
                }

                // Vista previa local mientras sube. Quill 1 no acepta blob:, por eso data:
                const previa = await readAsDataUrl(file);
                // Cada imagen en su propia línea y el cursor debajo, listo para seguir escribiendo
                if (index > 0 && quill.getText(index - 1, 1) !== '\n') {
                    quill.insertText(index, '\n', 'user');
                    index += 1;
                }
                quill.insertEmbed(index, 'image', previa, 'user');
                quill.insertText(index + 1, '\n', 'user');
                index += 2;
                quill.setSelection(index, 0, 'user');
                subirImagen(file, previa);
            }

            if (rechazadas.length) alert('No se agregaron:\n' + rechazadas.join('\n'));
        };

        const imagenesDe = (event, key) => Array.from(event[key]?.files || []).filter((f) => f.type.startsWith('image/'));

        // En captura sobre el contenedor para que el portapapeles de Quill no procese el archivo
        const onPaste = (event) => {
            const files = imagenesDe(event, 'clipboardData');
            if (!files.length) return;
            event.preventDefault();
            event.stopPropagation();
            insertarArchivos(files);
        };

        const onDrop = (event) => {
            dragging.value = false;
            const files = imagenesDe(event, 'dataTransfer');
            if (!files.length) return;
            event.preventDefault();
            event.stopPropagation();
            // Inserta donde se soltó (Chrome/Edge/Safari); si no, en el cursor
            try {
                const pos = document.caretRangeFromPoint && document.caretRangeFromPoint(event.clientX, event.clientY);
                const blot = pos && Quill.find(pos.startContainer, true);
                if (blot) quill.setSelection(quill.getIndex(blot) + pos.startOffset, 0, 'silent');
            } catch (e) {
                // sin posición: queda el cursor actual
            }
            insertarArchivos(files);
        };

        const onDragOver = (event) => {
            if (Array.from(event.dataTransfer?.types || []).includes('Files')) {
                event.preventDefault();
                dragging.value = true;
            }
        };

        const onDragLeave = () => { dragging.value = false; };

        const elegirImagenes = () => {
            const input = Object.assign(document.createElement('input'), {
                type: 'file',
                accept: ALLOWED_TYPES.join(','),
                multiple: true,
            });
            input.onchange = () => insertarArchivos(Array.from(input.files || []));
            input.click();
        };

        // --- API pública (se usa con ref desde la vista) ---

        const getHTML = () => quill.root.innerHTML;

        const getText = () => quill.getText().trim();

        const hasContent = () => getText().length > 0 || !!quill.root.querySelector('img');

        const setContent = (value) => {
            if (!value) {
                quill.setText('', 'api');
            } else if (isHtml(value)) {
                quill.setContents(quill.clipboard.convert(value), 'api');
            } else {
                // Respuesta vieja en texto plano
                quill.setText(String(value), 'api');
            }
            quill.history.clear();
        };

        // Después de enviar: quita del servidor las imágenes subidas que ya no están en el texto
        const confirmarEnvio = async (htmlEnviado) => {
            const huerfanas = pendientes.filter((img) => !htmlEnviado.includes(img.url));
            pendientes = [];
            await descartar(huerfanas);
        };

        onMounted(() => {
            quill = new Quill(editorEl.value, {
                theme: 'snow',
                placeholder: props.placeholder,
                formats: FORMATS,
                modules: {
                    toolbar: { container: TOOLBAR, handlers: { image: elegirImagenes } },
                },
            });

            quill.on('text-change', () => emit('change', getHTML()));

            const container = editorEl.value;
            container.addEventListener('paste', onPaste, true);
            container.addEventListener('drop', onDrop, true);
            container.addEventListener('dragover', onDragOver);
            container.addEventListener('dragleave', onDragLeave);
        });

        onBeforeUnmount(() => {
            const container = editorEl.value;
            if (container) {
                container.removeEventListener('paste', onPaste, true);
                container.removeEventListener('drop', onDrop, true);
                container.removeEventListener('dragover', onDragOver);
                container.removeEventListener('dragleave', onDragLeave);
            }
            // Borrador abandonado: sus imágenes nunca se publicaron
            if (pendientes.length) descartar(pendientes);
            pendientes = [];
        });

        return {
            editorEl,
            uploading,
            dragging,
            getHTML,
            getText,
            hasContent,
            setContent,
            confirmarEnvio,
        };
    },
};
</script>

<style>
.pqr-rich-editor .ql-toolbar.ql-snow {
    border-radius: 0.5rem 0.5rem 0 0;
    border-color: #d2d6da;
}

.pqr-rich-editor .ql-container.ql-snow {
    border-radius: 0 0 0.5rem 0.5rem;
    border-color: #d2d6da;
    font-family: inherit;
    font-size: 0.875rem;
}

.pqr-rich-editor .ql-editor {
    min-height: 180px;
    max-height: 480px;
    overflow-y: auto;
}

/* Miniatura dentro del editor; el tamaño real se conserva en el archivo subido */
.pqr-rich-editor .ql-editor img {
    max-width: min(100%, 320px);
    max-height: 220px;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 0.5rem;
    border: 1px solid #e9ecef;
    display: block;
    margin: 0.25rem 0;
}

.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-label::before,
.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-item::before {
    content: 'Normal';
}

.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
    content: 'Titulo';
}

.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
.pqr-rich-editor .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
    content: 'Subtitulo';
}

.pqr-rich-editor.is-dragging .ql-container.ql-snow {
    border: 2px dashed #17c1e8;
}
</style>
